import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoginFormService } from 'src/app/routes/auth/forms/login.form';
import { ModalRegisterComponent } from 'src/app/routes/auth/modals/modal-register/modal-register.component';
import { ModalRestorePasswordComponent } from 'src/app/routes/auth/modals/modal-restore-password/modal-restore-password.component';
import { AuthService } from 'src/app/routes/auth/state/auth.service';
import { environment } from 'src/environments/environment';
import { AccButtonComponent } from 'src/app/_shared/components/acc-button/acc-button.component';
import { IconButtonComponent } from 'src/app/_shared/components/icon-button/icon-button.component';
import { InputTextComponent } from 'src/app/_shared/components/input-text/input-text.component';
import { ThemeBaseComponent } from 'src/app/_core';
import {
  CustomOverlayRef,
  OverlayService,
} from 'src/app/_core/services/overlay.service';
import { A11yModule } from '@angular/cdk/a11y';

declare const google: any;
declare const FB: any;

@Component({
  standalone: true,
  selector: 'neopos-modal-login',
  templateUrl: './modal-login.component.html',
  styles: [
    `
      :host {
        display: block;
        width: -webkit-fill-available;
        overflow: hidden;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    IconButtonComponent,
    InputTextComponent,
    AccButtonComponent,
    A11yModule,
  ],
})
export class ModalLoginComponent
  extends ThemeBaseComponent
  implements OnDestroy
{
  private readonly _loginFormService = inject(LoginFormService);
  private readonly _dialogRef = inject(CustomOverlayRef);
  private readonly _overlayService = inject(OverlayService);
  private readonly _authService = inject(AuthService);
  private readonly _router = inject(Router);
  private readonly _translateService = inject(TranslateService);

  loginFormVM = this._loginFormService.vm;

  private _mutationLogin = this._authService.login();
  private _mutationGoogleLogin = this._authService.googleLogin();
  private _mutationFacebookLogin = this._authService.facebookLogin();

  ngOnDestroy(): void {
    this._loginFormService.resetForm();
  }

  close = (): void => this._dialogRef.close();

  onOpenRestorePasswordModal(): void {
    this.close();

    this._overlayService.open(
      {
        width: 434,
        hasBackdrop: true,
        backdropClass: 'backdrop-style',
        responsive: true,
      },
      ModalRestorePasswordComponent
    );
  }

  onOpenRegisterModal(): void {
    this.close();

    this._overlayService.open(
      {
        width: 434,
        hasBackdrop: true,
        backdropClass: 'backdrop-style',
        responsive: true,
      },
      ModalRegisterComponent
    );
  }

  onLogin(): void {
    if (this._loginFormService.canSubmitForm()) {
      this._mutationLogin.mutate(this.loginFormVM.getRawValue()).then(() => {
        this._router.navigate([this._translateService.instant('/konto')]);
        this.close();
      });
    }
  }

  onGoogleLogin(): void {
    google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: (response: any) => {
        if (response.credential) {
          this._mutationGoogleLogin.mutate(response.credential).then(() => {
            this._router.navigate([this._translateService.instant('/konto')]);
            this.close();
          });
        }
      },
    });

    google.accounts.id.prompt();
  }

  onFacebookLogin(): void {
    FB.init({
      appId: environment.facebookAppId,
      cookie: true,
      xfbml: false,
      version: 'v19.0',
    });

    FB.login(
      (response: any) => {
        if (response.authResponse) {
          const accessToken = response.authResponse.accessToken;
          this._mutationFacebookLogin.mutate(accessToken).then(() => {
            this._router.navigate([this._translateService.instant('/konto')]);
            this.close();
          });
        } else {
          console.error('Facebook login cancelled or failed', response);
        }
      },
      { scope: 'email,public_profile' }
    );
  }
}
